import { Drawer, Box } from "@mui/material";
import { DefaultValues, useForm, UseFormReturn } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ReactNode, useCallback, useContext, useLayoutEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";
import { AppContext } from "../../contexts/app_context/app_context";

import PrimaryButton from "../primary_button";


/**
 * useDrawerState
 */

export interface BaseDrawerState<TForm> {
  open: boolean;
  onClose: () => void;
  onSubmit?: (form: TForm) => Promise<void>;
};

type StartDrawerFunc<TForm> = (onSubmit: (form: TForm) => Promise<void>) => void;

type CloseDrawerFunc = () => void;

export const CreateBaseDrawerHook = <TForm,>() => {
  return function useHook(): [BaseDrawerState<TForm>, StartDrawerFunc<TForm>, CloseDrawerFunc] {
    const [open, setOpen] = useState(false);
    const submitCallbackRef = useRef<(form: TForm) => Promise<void>>(undefined);

    const state = useMemo((): BaseDrawerState<TForm> => {
      return {
        open: open,
        onClose: () => setOpen(false),
        onSubmit: submitCallbackRef.current,
      }
    }, [
      open,
    ]);

    const handleCloseDrawer = useCallback(() => {
      submitCallbackRef.current = undefined;
      setOpen(false);
    }, []);

    const handleStartDrawer = useCallback((onSubmit: (form: TForm) => Promise<void>, initialValues?: Partial<TForm>) => {
      submitCallbackRef.current = async (f) => {
        await onSubmit(f);
        setOpen(false);
      };
      setOpen(true);
    }, [
    ]);

    return [state, handleStartDrawer, handleCloseDrawer];
  };
}

/**
 * BaseDrawer
 */

type Props<TForm extends {}> = {
  title: string;
  schema: z.ZodType<TForm, z.ZodTypeDef, any>;
  state: BaseDrawerState<TForm>;
  saving: boolean;
  defaultValues?: DefaultValues<TForm>;
  children?: (hookForm: UseFormReturn<TForm>) => ReactNode;
};

export const BaseDrawer = <TForm extends {},>({
  title,
  schema,
  state,
  saving,
  defaultValues,
  children,
}: Props<TForm>) => {
  const { theme } = useContext(AppContext);

  const hookForm = useForm<TForm>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: defaultValues,
    resolver: zodResolver(schema),
  });

  useLayoutEffect(() => {
    if (state.open) {
      hookForm.reset(defaultValues);
    }
  }, [
    state,
    defaultValues,
    hookForm,
  ]);

  const handleClose = useCallback(() => {
    if (!saving) {
      state.onClose();
    }
  }, [
    saving,
    state,
  ]);

  const handleCreateCentroCusto = useCallback(async (form: TForm) => {
    if (!saving && state.onSubmit) {
      await state.onSubmit(form);
    }
  }, [
    saving,
    state,
  ]);

  return (
    <Drawer
      anchor="right"
      open={state.open}
      onClose={() => handleClose()}
      style={{ zIndex: 1300 }}
      slotProps={{
        paper: {
          style: {
            display: "flex",
            flexDirection: "column",
            height: "100%",
            width: 500,
          }
        }
      }}>
      <form onSubmit={hookForm.handleSubmit(handleCreateCentroCusto)}>
        <Box sx={{ flexGrow: 1, overflowY: "auto", margin: 4 }}>
          <h2 className="text-lg font-semibold mb-4">{title}</h2>

          <div className="flex flex-col items-start w-full gap-4 px-2">
            {children?.(hookForm)}
          </div>
        </Box>

        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            width: "100%",
            paddingLeft: 4,
            paddingRight: 4,
            paddingBottom: 8,
            marginTop: 0,
            backgroundColor: theme === 'dark' ? "#343434" : "#ffffff",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}>

          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              borderTop: theme === 'dark' ? "1px solid #606060" : "1px solid #e0e0e0",
              paddingTop: 2,
              marginHorizontal: 4,
            }}>

            <Box
              className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold"
              onClick={() => handleClose()}>
              <h1 className="text-sm text-black dark:text-white">Cancelar</h1>
            </Box>

            <Box width="120px">
              {saving ? (
                <PrimaryButton
                  type="button"
                  buttonColor="primary-green">
                  Salvando...
                </PrimaryButton>
              ) : (
                <PrimaryButton
                  type="submit"
                  buttonColor="primary-green">
                  Salvar
                </PrimaryButton>
              )}
            </Box>
          </Box>
        </Box>
      </form>
    </Drawer>
  );
}